import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Card, Checkbox, Col, Input, InputNumber, Row, Select, Space, Switch, Table, Tabs, Tooltip, Typography } from 'antd';
import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { CardTitle } from '../../components/StatCard';
import { NameCell } from '../../components/NameCell';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { emptyRuleGroup, formProject, formTheme, type FormField, type FormSettings, type FormSubmissionItem, type PublicForm } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { CreatableSelect } from '../../components/CreatableSelect';
import { PublicFormHeader, PublicFormRenderer, brandBand } from '../../components/PublicFormRenderer';
import { ProjectSelect } from '../../components/ProjectSelect';
import { useProjectScope } from '../../features/projects/scope';
import { AgentSelect } from '../../components/AgentSelect';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useAgents } from '../../features/email/api';
import { errorMessage } from '../../api/client';
import { useFields } from '../../features/fields/api';
import { useCreateList, useCreateSegment, useCreateTag, useLists, useSegments, useTags } from '../../features/audience/api';
import { usePipelines } from '../../features/deals/api';
import { embedSnippet, hostedFormUrl, useCreateForm, useForm, useSubmissions, useUpdateForm } from '../../features/forms/api';
import { contactName } from '../../features/contacts/api';
import { EmptyState, PageSkeleton, RecordError, tableLoading, tableLocale } from '../../components/states';
import { fmtDateTime, plural } from '../../utils/format';
import { useFeatures } from '../../features/settings/api';

const NOT_ALLOWED = new Set(['organization_id', 'email_status', 'subscribed', 'created_at', 'updated_at']);
const slugify = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);

interface Draft {
  name: string;
  slug: string;
  description: string;
  fields: FormField[];
  tagIds: string[];
  listIds: string[];
  segmentIds: string[];
  projectId: string | null;
  pipelineId: string | null;
  stageId: string | null;
  successMessage: string;
  redirectUrl: string;
  isActive: boolean;
  settings: FormSettings;
}

const empty: Draft = { name: '', slug: '', description: '', fields: [{ key: 'first_name', required: true, width: 'half' }, { key: 'last_name', width: 'half' }, { key: 'email', required: true }], tagIds: [], listIds: [], segmentIds: [], projectId: null, pipelineId: null, stageId: null, successMessage: '', redirectUrl: '', isActive: true, settings: { submitLabel: 'Submit' } };

function SortableRow(props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-row-key': string }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props['data-row-key'] });
  const style: React.CSSProperties = { ...props.style, transform: CSS.Translate.toString(transform), transition, ...(isDragging ? { position: 'relative', zIndex: 10, background: '#fafafa' } : {}) };
  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
}

export function FormEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { message } = App.useApp();
  const form = useForm(isNew ? undefined : id);
  const defs = useFields('CONTACT');
  const tags = useTags();
  const lists = useLists();
  const segments = useSegments();
  const createTag = useCreateTag();
  const createList = useCreateList();
  const createSegment = useCreateSegment();
  const scope = useProjectScope();
  const agents = useAgents();
  const dealsOn = useFeatures().enabled('pipelines');
  const pipelines = usePipelines(dealsOn);
  const create = useCreateForm();
  const update = useUpdateForm();
  const [draft, setDraft] = useState<Draft>(empty);
  const [loaded, setLoaded] = useState(isNew);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [subPage, setSubPage] = useState(1);
  const [subPageSize, setSubPageSize] = useState(25);
  const submissions = useSubmissions(isNew ? undefined : id, subPage, subPageSize);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    if (form.data && !loaded) {
      const f = form.data;
      setDraft({ name: f.name, slug: f.slug, description: f.description ?? '', fields: f.fields, tagIds: f.tagIds, listIds: f.listIds ?? [], segmentIds: f.segmentIds ?? [], projectId: f.projectId, pipelineId: f.pipelineId, stageId: f.stageId, successMessage: f.successMessage ?? '', redirectUrl: f.redirectUrl ?? '', isActive: f.isActive, settings: f.settings ?? {} });
      setLoaded(true);
    }
  }, [form.data, loaded]);

  // a new form starts in the project picked in the header
  const projectDefaulted = useRef(!isNew);
  useEffect(() => {
    if (projectDefaulted.current || !scope.ready) return;
    projectDefaulted.current = true;
    if (scope.current) setDraft((d) => ({ ...d, projectId: scope.current!.id }));
  }, [scope.ready, scope.current]);

  const available = useMemo(() => (defs.data ?? []).filter((d) => !NOT_ALLOWED.has(d.key)), [defs.data]);
  const defByKey = useMemo(() => new Map(available.map((d) => [d.key, d])), [available]);
  const unused = available.filter((d) => !draft.fields.some((f) => f.key === d.key));
  const stages = pipelines.data?.find((p) => p.id === draft.pipelineId)?.stages ?? [];
  const project = scope.enabled ? scope.byId(draft.projectId) : undefined;
  const projectColor = formTheme(undefined, project).primaryColor;

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const followUp = draft.settings.followUp;
  const setFollowUp = (patch: Partial<NonNullable<FormSettings['followUp']>>) =>
    set({ settings: { ...draft.settings, followUp: { enabled: false, ...draft.settings.followUp, ...patch } } });
  const turnOnFollowUp = (on: boolean) =>
    setFollowUp(
      on && !followUp?.subject && !followUp?.html
        ? {
            enabled: true,
            agentId: followUp?.agentId ?? agents.data?.find((a) => a.isActive)?.id ?? null,
            subject: 'Thanks, {{first_name|there}}',
            html: `<p>Hi {{first_name|there}},</p><p>Thanks for filling in ${draft.name ? `"${draft.name}"` : 'our form'}. We received your details and will be in touch soon.</p>`,
          }
        : { enabled: on },
    );
  const setField = (i: number, patch: Partial<FormField>) => set({ fields: draft.fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) });

  const preview = useMemo<PublicForm>(
    () => ({
      name: draft.name || 'Untitled form',
      slug: draft.slug,
      description: draft.description || null,
      fields: draft.fields
        .map((f) => {
          const d = defByKey.get(f.key);
          return d ? { key: f.key, label: f.label || d.label, type: d.type, options: d.options, required: !!f.required, placeholder: f.placeholder, helpText: f.helpText, width: f.width ?? 'full' } : null;
        })
        .filter((x): x is NonNullable<typeof x> => !!x),
      submitLabel: draft.settings.submitLabel || 'Submit',
      consentText: draft.settings.requireConsent ? draft.settings.consentText || 'I agree to receive emails.' : null,
      successMessage: draft.successMessage || 'Thanks, we received your submission.',
      redirectUrl: draft.redirectUrl || null,
      theme: formTheme(draft.settings.theme, project),
      project: formProject(project),
    }),
    [draft, defByKey, project],
  );

  const save = async () => {
    if (!draft.name.trim()) return message.warning('Give the form a name');
    if (!draft.fields.length) return message.warning('Add at least one field');
    if (followUp?.enabled && (!followUp.agentId || !followUp.subject?.trim())) return message.warning('Choose who the follow-up email comes from and give it a subject');
    const payload = {
      name: draft.name.trim(),
      slug: draft.slug || undefined,
      description: draft.description || null,
      fields: draft.fields,
      tagIds: draft.tagIds,
      listIds: draft.listIds,
      segmentIds: draft.segmentIds,
      // with projects off (under two) the label is left as it is
      projectId: scope.enabled ? draft.projectId : undefined,
      pipelineId: draft.pipelineId,
      stageId: draft.pipelineId ? draft.stageId : null,
      successMessage: draft.successMessage || null,
      redirectUrl: draft.redirectUrl || null,
      isActive: draft.isActive,
      settings: draft.settings,
    };
    try {
      if (isNew) {
        const f = await create.mutateAsync(payload);
        // the slug is now public; stop deriving it from the name
        setSlugTouched(true);
        set({ slug: f.slug });
        message.success('Form created');
        navigate(`/forms/${f.id}`, { replace: true });
      } else {
        await update.mutateAsync({ id: id!, ...payload });
        message.success('Form saved');
      }
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  if (!isNew && form.isError) return <RecordError error={form.error} what="Form" backTo="/forms" backLabel="Back to forms" onRetry={form.refetch} />;
  if (!loaded) return <PageSkeleton />;

  const builder = (
    <Row gutter={24}>
      <Col xs={24} xl={14}>
        <Card title={<CardTitle title="Form" subtitle="Name, public link and intro text" />} style={{ marginBottom: 24 }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <Typography.Text type="secondary">Name</Typography.Text>
                <Input aria-label="Form name" value={draft.name} onChange={(e) => set({ name: e.target.value, ...(isNew && !slugTouched ? { slug: slugify(e.target.value) } : {}) })} placeholder="Request a demo" maxLength={120} />
              </div>
              <div>
                <Typography.Text type="secondary">URL slug</Typography.Text>
                <Space.Compact style={{ width: '100%' }}><Input value="/f/" readOnly style={{ width: 50, textAlign: 'center', pointerEvents: 'none' }} /><Input aria-label="URL slug" value={draft.slug} onChange={(e) => { setSlugTouched(true); set({ slug: slugify(e.target.value) }); }} maxLength={80} /></Space.Compact>
                {!isNew && form.data && draft.slug !== form.data.slug && <Typography.Text type="warning" style={{ fontSize: 12 }}>Changing the slug breaks links and embeds that use /f/{form.data.slug}.</Typography.Text>}
              </div>
            </div>
            {scope.enabled && (
              <div>
                <Typography.Text type="secondary">Project</Typography.Text>
                {/* a newly picked project brings its own colour, so drop a colour picked by hand */}
                <ProjectSelect style={{ width: '100%' }} value={draft.projectId} onChange={(v) => set({ projectId: v, settings: { ...draft.settings, theme: { ...draft.settings.theme, primaryColor: undefined } } })} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>The hosted page shows the project's logo above the form and uses its colours. New tags, lists and segments made here go into this project.</Typography.Text>
              </div>
            )}
            <div>
              <Typography.Text type="secondary">Intro text (optional)</Typography.Text>
              <Input.TextArea aria-label="Intro text" value={draft.description} onChange={(e) => set({ description: e.target.value })} autoSize={{ minRows: 2 }} maxLength={1000} />
            </div>
          </Space>
        </Card>

        <Card title={<CardTitle title="Fields" subtitle="What visitors fill in" />} style={{ marginBottom: 24 }} extra={<Typography.Text type="secondary" style={{ fontSize: 12 }}>Drag to reorder</Typography.Text>}>
          <DndContext sensors={sensors} onDragEnd={({ active, over }: DragEndEvent) => { if (over && active.id !== over.id) set({ fields: arrayMove(draft.fields, draft.fields.findIndex((f) => f.key === active.id), draft.fields.findIndex((f) => f.key === over.id)) }); }}>
            <SortableContext items={draft.fields.map((f) => f.key)} strategy={verticalListSortingStrategy}>
              <Table<FormField>
                rowKey="key"
                size="small"
                dataSource={draft.fields}
                pagination={false}
                components={{ body: { row: SortableRow } }}
                columns={[
                  { key: 'drag', width: 28, render: () => <HolderOutlined style={{ cursor: 'grab', color: '#999' }} /> },
                  { title: 'Field', key: 'key', width: 160, render: (_, f) => <span>{defByKey.get(f.key)?.label ?? f.key}<br /><Typography.Text type="secondary" style={{ fontSize: 11 }}>{f.key}</Typography.Text></span> },
                  { title: 'Label on form', key: 'label', render: (_, f, i) => <Input size="small" value={f.label ?? ''} placeholder={defByKey.get(f.key)?.label} onChange={(e) => setField(i, { label: e.target.value || undefined })} /> },
                  { title: 'Placeholder', key: 'ph', render: (_, f, i) => <Input size="small" value={f.placeholder ?? ''} onChange={(e) => setField(i, { placeholder: e.target.value || undefined })} /> },
                  { title: 'Required', key: 'req', width: 80, render: (_, f, i) => <Checkbox checked={!!f.required} onChange={(e) => setField(i, { required: e.target.checked })} /> },
                  { title: 'Width', key: 'w', width: 90, render: (_, f, i) => <Select size="small" value={f.width ?? 'full'} onChange={(w) => setField(i, { width: w })} options={[{ value: 'full', label: 'Full' }, { value: 'half', label: 'Half' }]} /> },
                  { key: 'del', width: 40, render: (_, f) => <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Remove field" title="Remove field" onClick={() => set({ fields: draft.fields.filter((x) => x.key !== f.key) })} /> },
                ]}
              />
            </SortableContext>
          </DndContext>
          <Select
            style={{ marginTop: 12, minWidth: 260 }}
            placeholder="Add a field…"
            value={null}
            showSearch
            optionFilterProp="label"
            onChange={(key: string) => key && set({ fields: [...draft.fields, { key }] })}
            options={unused.map((d) => ({ value: d.key, label: `${d.label}${d.isSystem ? '' : ' (custom)'}` }))}
            suffixIcon={<PlusOutlined />}
          />
        </Card>

        <Card title={<CardTitle title="After submit" subtitle="What visitors see once they send the form" />} style={{ marginBottom: 24 }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <div><Typography.Text type="secondary">Submit button label</Typography.Text><Input value={draft.settings.submitLabel ?? ''} onChange={(e) => set({ settings: { ...draft.settings, submitLabel: e.target.value } })} placeholder="Submit" maxLength={60} /></div>
            <div><Typography.Text type="secondary">Success message</Typography.Text><Input.TextArea value={draft.successMessage} onChange={(e) => set({ successMessage: e.target.value })} autoSize={{ minRows: 2 }} placeholder="Thanks, we received your submission." maxLength={1000} /></div>
            <div><Typography.Text type="secondary">Or redirect to URL</Typography.Text><Input value={draft.redirectUrl} onChange={(e) => set({ redirectUrl: e.target.value })} placeholder="https://yoursite.com/thank-you" /></div>
            <Space>
              <Switch checked={!!draft.settings.requireConsent} onChange={(c) => set({ settings: { ...draft.settings, requireConsent: c } })} />
              <Typography.Text>Ask for email consent (visitors must tick the box to submit)</Typography.Text>
            </Space>
            {draft.settings.requireConsent && <Input value={draft.settings.consentText ?? ''} onChange={(e) => set({ settings: { ...draft.settings, consentText: e.target.value } })} placeholder="I agree to receive emails." maxLength={500} />}
          </Space>
        </Card>

        <Card title={<CardTitle title="Follow-up email" subtitle="Sent to the person who submitted, right after they send the form" />} style={{ marginBottom: 24 }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Space>
              <Switch aria-label="Send a follow-up email" checked={!!followUp?.enabled} onChange={turnOnFollowUp} />
              <Typography.Text>Email the person who submitted the form</Typography.Text>
            </Space>
            {followUp?.enabled && (
              <>
                {!draft.fields.some((f) => f.key === 'email') && <Typography.Text type="warning">Add the Email field to the form: without it the follow-up only reaches people already in the CRM.</Typography.Text>}
                <div><Typography.Text type="secondary">From</Typography.Text><AgentSelect value={followUp.agentId} onChange={(v) => setFollowUp({ agentId: v })} /></div>
                <div><Typography.Text type="secondary">Subject</Typography.Text><Input aria-label="Follow-up subject" value={followUp.subject ?? ''} onChange={(e) => setFollowUp({ subject: e.target.value })} placeholder="Thanks, {{first_name|there}}" maxLength={500} /></div>
                <div>
                  <Typography.Text type="secondary">Message</Typography.Text>
                  <RichTextEditor value={followUp.html ?? ''} onChange={(html) => setFollowUp({ html })} placeholder="Hi {{first_name|there}}, …" minHeight={160} />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>Merge tags like {'{{first_name}}'} work, and the agent's signature is added. People who unsubscribed get no email. The email shows up on the contact's timeline.</Typography.Text>
                </div>
              </>
            )}
          </Space>
        </Card>

        <Card title={<CardTitle title="What happens in the CRM" subtitle={dealsOn ? 'Tags, lists, segments, duplicate handling and an optional deal' : 'Tags, lists, segments and duplicate handling'} />} style={{ marginBottom: 24 }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Typography.Text type="secondary">A submission with an email that already exists updates that contact (filling empty fields) instead of creating a duplicate.</Typography.Text>
            <Space>
              <Switch checked={!!draft.settings.overwriteExisting} onChange={(c) => set({ settings: { ...draft.settings, overwriteExisting: c } })} />
              <Typography.Text>Overwrite existing values with submitted ones</Typography.Text>
            </Space>
            <div><Typography.Text type="secondary">Apply tags</Typography.Text><CreatableSelect multiple ariaLabel="Apply tags" items={tags.data} value={draft.tagIds} onChange={(v) => set({ tagIds: v })} placeholder="None · type to create a tag" create={{ noun: 'tag', pending: createTag.isPending, run: async (name) => (await createTag.mutateAsync({ name, projectId: project?.id ?? null })).id }} /></div>
            <div><Typography.Text type="secondary">Add to lists</Typography.Text><CreatableSelect multiple ariaLabel="Add to lists" items={lists.data} value={draft.listIds} onChange={(v) => set({ listIds: v })} placeholder="None · type to create a list" create={{ noun: 'list', pending: createList.isPending, run: async (name) => (await createList.mutateAsync({ name, projectId: project?.id ?? null })).id }} /></div>
            <div>
              <Typography.Text type="secondary">Add to segments</Typography.Text>
              <CreatableSelect multiple ariaLabel="Add to segments" items={segments.data} value={draft.segmentIds} onChange={(v) => set({ segmentIds: v })} placeholder="None · type to create a segment" create={{ noun: 'segment', pending: createSegment.isPending, run: async (name) => (await createSegment.mutateAsync({ name, rules: emptyRuleGroup(), projectId: project?.id ?? null })).id }} />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Submitters stay in these segments even if they don't match the segment's rules.</Typography.Text>
            </div>
            {dealsOn && <Space>
              <Tooltip title={!pipelines.isLoading && !pipelines.data?.length ? 'Create a pipeline under Admin → Pipelines first' : undefined}>
                <Switch aria-label="Create a deal for each submission" disabled={!pipelines.data?.length} checked={!!draft.pipelineId} onChange={(c) => set({ pipelineId: c ? (pipelines.data?.find((p) => p.isDefault)?.id ?? pipelines.data?.[0]?.id ?? null) : null, stageId: null })} />
              </Tooltip>
              <Typography.Text>Create a deal for each submission</Typography.Text>
            </Space>}
            {dealsOn && draft.pipelineId && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div><Typography.Text type="secondary">Pipeline</Typography.Text><Select style={{ width: '100%' }} value={draft.pipelineId} onChange={(v) => set({ pipelineId: v, stageId: null })} options={(pipelines.data ?? []).map((p) => ({ value: p.id, label: p.name }))} /></div>
                <div><Typography.Text type="secondary">Stage</Typography.Text><Select style={{ width: '100%' }} allowClear placeholder="First open stage" value={draft.stageId ?? undefined} onChange={(v) => set({ stageId: v ?? null })} options={stages.map((s) => ({ value: s.id, label: s.name }))} /></div>
                <div><Typography.Text type="secondary">Deal title</Typography.Text><Input value={draft.settings.dealTitle ?? ''} onChange={(e) => set({ settings: { ...draft.settings, dealTitle: e.target.value } })} placeholder={`{{full_name|New lead}} – ${draft.name || 'form'}`} /></div>
                <div><Typography.Text type="secondary">Deal value</Typography.Text><InputNumber style={{ width: '100%' }} min={0} value={draft.settings.dealValue ?? undefined} onChange={(v) => set({ settings: { ...draft.settings, dealValue: v ?? null } })} /></div>
                <div><Typography.Text type="secondary">Agent</Typography.Text><AgentSelect value={draft.settings.agentId} onChange={(v) => set({ settings: { ...draft.settings, agentId: v } })} /></div>
              </div>
            )}
          </Space>
        </Card>
      </Col>
      <Col xs={24} xl={10}>
        <Card title={<CardTitle title="Preview" subtitle="How the hosted page looks to visitors" />} style={{ marginBottom: 24 }}>
          <div style={{ background: preview.theme.background ?? '#f5f6f8', padding: 16, borderRadius: 8 }}>
            <Card style={brandBand(preview)}>
              <PublicFormHeader form={preview} />
              <PublicFormRenderer key={JSON.stringify(preview)} form={preview} preview />
            </Card>
          </div>
          <Space style={{ marginTop: 12 }}>
            <Typography.Text type="secondary">Button color</Typography.Text>
            <Input type="color" aria-label="Button color" value={preview.theme.primaryColor ?? '#1677ff'} onChange={(e) => set({ settings: { ...draft.settings, theme: { ...draft.settings.theme, primaryColor: e.target.value } } })} style={{ width: 48, padding: 2 }} />
            {projectColor && draft.settings.theme?.primaryColor && draft.settings.theme.primaryColor !== projectColor && (
              <Button type="link" size="small" onClick={() => set({ settings: { ...draft.settings, theme: { ...draft.settings.theme, primaryColor: undefined } } })}>Use project color</Button>
            )}
          </Space>
        </Card>
        {!isNew && (
          <Card title={<CardTitle title="Share" subtitle="Hosted link and embed code" />}>
            <Typography.Text type="secondary">Hosted page</Typography.Text>
            <Typography.Paragraph copyable><a href={hostedFormUrl(draft.slug)} target="_blank" rel="noreferrer">{hostedFormUrl(draft.slug)}</a></Typography.Paragraph>
            <Typography.Text type="secondary">Embed on your website</Typography.Text>
            <Typography.Paragraph copyable={{ text: embedSnippet(draft.slug) }}>
              <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#fafafa', padding: 8, borderRadius: 6, margin: 0 }}>{embedSnippet(draft.slug)}</pre>
            </Typography.Paragraph>
          </Card>
        )}
      </Col>
    </Row>
  );

  const submissionsTab = (
    <Card title={<CardTitle title="Submissions" subtitle="Every time someone sent this form, newest first" />}>
    <Table<FormSubmissionItem>
      rowKey="id"
      scroll={{ x: 900 }}
      loading={tableLoading(submissions)}
      locale={tableLocale(submissions, <EmptyState description="No submissions yet. Share the hosted link or embed the form on your site." />)}
      dataSource={submissions.data?.items ?? []}
      pagination={{ current: subPage, pageSize: subPageSize, total: submissions.data?.total ?? 0, showSizeChanger: true, onChange: (p, s) => { setSubPage(s === subPageSize ? p : 1); setSubPageSize(s); } }}
      columns={[
        { title: 'When', dataIndex: 'createdAt', width: 160, render: (v) => fmtDateTime(v) },
        { title: 'Contact', key: 'contact', width: 260, render: (_, s) => (s.contact ? <NameCell size={32} name={contactName(s.contact)} sub={s.contact.email} to={`/contacts/${s.contact.id}`} /> : '—') },
        { title: 'Submitted values', key: 'payload', render: (_, s) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>{Object.entries(s.payload).map(([k, v]) => `${defByKey.get(k)?.label ?? k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`).join(' · ')}</Typography.Text> },
        { title: 'IP', dataIndex: 'ip', width: 130, render: (v) => v ?? '—' },
      ]}
    />
    </Card>
  );

  return (
    <div className="page page-wide">
      <PageHeader
        breadcrumb={[{ title: 'Forms', to: '/forms' }, { title: isNew ? 'New form' : form.data?.name ?? 'Form' }]}
        title={isNew ? 'New form' : form.data?.name ?? ''}
        subtitle={!isNew && form.data ? plural(form.data.submissionCount, 'submission') : undefined}
        extra={
          <Space>
            <Space><Switch aria-label="Form active" checked={draft.isActive} onChange={(c) => set({ isActive: c })} /><Typography.Text>Active</Typography.Text></Space>
            <Button type="primary" onClick={() => void save()} loading={create.isPending || update.isPending}>{isNew ? 'Create form' : 'Save'}</Button>
          </Space>
        }
      />
      {isNew ? builder : <Tabs items={[{ key: 'builder', label: 'Builder', children: builder }, { key: 'submissions', label: `Submissions (${form.data?.submissionCount ?? 0})`, children: submissionsTab }]} />}
    </div>
  );
}
